import * as React from 'react';
import propz from 'propz';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../../consts/report';
import { SchoolStudent } from 'Src/models/schoolStudent';
import { ReportActions, ReportDateRange, ReportDescription, ReportSection, ReportToggle, ReportToggleList } from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';

const OPTIONS = {
  IS_TODAY: 'isToday',
  IS_YESTERDAY: 'isYesterday',
  IS_CURRENT_WEEK: 'isCurrentWeek',
  IS_PAST_WEEK: 'isPastWeek',
  IS_CUSTOM_INTERVAL: 'isCustomInterval'
};

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  student: SchoolStudent[];
  activeSchoolId: string;
}

const startOfDay = (date: Date) => new Date(new Date(date).setHours(0, 0, 1, 0));
const endOfDay = (date: Date) => new Date(new Date(date).setHours(23, 59, 59, 999));
const mondayFor = (date: Date) => {
  const result = new Date(date);
  const day = result.getDay();
  result.setDate(result.getDate() - (day === 0 ? 6 : day - 1));
  return result;
};
const rangeForPeriod = (period: string) => {
  const today = new Date();
  if (period === OPTIONS.IS_YESTERDAY) {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return { dateFrom: startOfDay(yesterday), dateTo: endOfDay(yesterday) };
  }
  if (period === OPTIONS.IS_CURRENT_WEEK) {
    const monday = mondayFor(today);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { dateFrom: startOfDay(monday), dateTo: endOfDay(sunday) };
  }
  if (period === OPTIONS.IS_PAST_WEEK) {
    const monday = mondayFor(today);
    monday.setDate(monday.getDate() - 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { dateFrom: startOfDay(monday), dateTo: endOfDay(sunday) };
  }
  return { dateFrom: startOfDay(today), dateTo: endOfDay(today) };
};

export const StudentParticipationInEventsReportForm = ({ onCloseClick, onSubmit, student, activeSchoolId }: Props) => {
  const [schoolData, setSchoolData] = React.useState<any>(null);

  React.useEffect(() => {
    fetch('/dist/images/mis/misIntegrationSchoolsList.json')
      .then(response => response.json())
      .then(data => setSchoolData(data.find(item => item.schoolSITId === activeSchoolId)))
      .catch(error => console.error('Error loading JSON:', error));
  }, [activeSchoolId]);

  const todayRange = rangeForPeriod(OPTIONS.IS_TODAY);
  const initialValues = {
    options: OPTIONS.IS_TODAY,
    isYesterday: false,
    isCurrentWeek: false,
    isPastWeek: false,
    isCustomInterval: false,
    isPrintPP: schoolData ? schoolData.studentsPPInfo === 1 : false,
    isPrintSEN: schoolData ? schoolData.studentsSENInfo === 1 : false,
    isPrintFSM: schoolData ? schoolData.studentsFSMInfo === 1 : false,
    isShowSportEvents: true,
    isShowClubEvents: true,
    isShowTournamentEvents: false,
    isPrintAttendance: true,
    dateFrom: todayRange.dateFrom,
    dateTo: todayRange.dateTo
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={values => {
        const valuesToTransfer = { ...values };
        propz.set(valuesToTransfer, [valuesToTransfer.options], true);
        delete valuesToTransfer.options;

        if (values.options !== OPTIONS.IS_CUSTOM_INTERVAL) {
          const range = rangeForPeriod(values.options);
          valuesToTransfer.dateFrom = range.dateFrom;
          valuesToTransfer.dateTo = range.dateTo;
        }

        onSubmit(valuesToTransfer, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.STUDENT_PARTICIPATION_IN_EVENTS);
      }}
    >
      {({ values, setFieldValue }) => (
        <Form className="eReportForm">
          <div className="eReportForm__scroll">
          <ReportDescription>View activity data for the selected student across fixtures, tournaments and clubs.</ReportDescription>
          <ReportSection number={1} title="Reporting period" hint="Choose the date range included in the report." required>
            <ReportDateRange dateFrom={values.dateFrom} dateTo={values.dateTo} onChangeFrom={date => { setFieldValue('options', OPTIONS.IS_CUSTOM_INTERVAL); setFieldValue('dateFrom', date); if (values.dateTo < date) setFieldValue('dateTo', date); }} onChangeTo={date => { setFieldValue('options', OPTIONS.IS_CUSTOM_INTERVAL); setFieldValue('dateTo', date); }} />
          </ReportSection>
          <ReportSection number={2} title="Event types" hint="Choose which events and attendance data appear in the report.">
            <ReportToggleList>
              <ReportToggle name="isShowSportEvents" label="Show sport events" hint="Show all sport events within the selected period." value={values.isShowSportEvents} onChange={value => setFieldValue('isShowSportEvents', value)} />
              <ReportToggle name="isShowClubEvents" label="Show club events" hint="Show all club events within the selected period." value={values.isShowClubEvents} onChange={value => setFieldValue('isShowClubEvents', value)} />
              <ReportToggle name="isShowTournamentEvents" label="Show tournament events" hint="Show all tournament events within the selected period." value={values.isShowTournamentEvents} onChange={value => setFieldValue('isShowTournamentEvents', value)} />
              <ReportToggle name="isPrintAttendance" label="Show attendance" hint="Include recorded attendance in the report." value={values.isPrintAttendance} onChange={value => setFieldValue('isPrintAttendance', value)} />
            </ReportToggleList>
          </ReportSection>

          {(schoolData && (schoolData.studentsPPInfo === 1 || schoolData.studentsSENInfo === 1 || schoolData.studentsFSMInfo === 1)) && (
            <ReportSection number={3} title="Student information" hint="Choose additional student information to include."><ReportToggleList>
              {schoolData.studentsPPInfo === 1 && <ReportToggle name="isPrintPP" label="Show PP" hint="Show data relating to students with PP." value={values.isPrintPP} onChange={value => setFieldValue('isPrintPP', value)} />}
              {schoolData.studentsSENInfo === 1 && <ReportToggle name="isPrintSEN" label="Show SEN" hint="Show data relating to students with SEN." value={values.isPrintSEN} onChange={value => setFieldValue('isPrintSEN', value)} />}
              {schoolData.studentsFSMInfo === 1 && <ReportToggle name="isPrintFSM" label="Show FSM" hint="Show data relating to students with FSM." value={values.isPrintFSM} onChange={value => setFieldValue('isPrintFSM', value)} />}
            </ReportToggleList></ReportSection>
          )}
          </div>
          <ReportActions onCancel={onCloseClick} />
        </Form>
      )}
    </Formik>
  );
};
